import { useCallback, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import type { ValidatePromoCode } from '@/modules/auth/use-cases'
import type { PromoCodeValidation } from '@/shared/domain'
import { CANADIAN_PROVINCES, US_STATES, type StateOption } from '@/shared/config'
import type { OrgFormData, OrgType } from '../types'
import { titleCase } from './title-case'

export type OrgFormCountry = 'US' | 'CA'

export type OrgFormFieldErrors = {
  orgName: string
  state: string
  promoCode: string
}

const EMPTY_FIELD_ERRORS: OrgFormFieldErrors = {
  orgName: '',
  state: '',
  promoCode: '',
}

function isOrgFormCountry(value: string): value is OrgFormCountry {
  return value === 'US' || value === 'CA'
}

export type UseOrgFormStepProps = {
  initialValue: OrgFormData
  orgType: OrgType
  onValidatePromoCode: ValidatePromoCode
  onPersistData: (data: OrgFormData) => void
  onSubmit: (data: OrgFormData) => void
}

export type UseOrgFormStepResult = {
  orgName: string
  country: OrgFormCountry
  state: string
  promoCode: string
  stateOptions: StateOption[]
  isSubmitting: boolean
  isApplyingPromo: boolean
  promoApplied: boolean
  appliedPromo: PromoCodeValidation | null
  fieldErrors: OrgFormFieldErrors
  submitError: string
  setOrgName: (value: string) => void
  setCountry: (value: OrgFormCountry) => void
  setState: (value: string) => void
  setPromoCode: (value: string) => void
  handleApplyPromo: () => Promise<void>
  handleSubmit: (event: FormEvent) => void
}

export function useOrgFormStep({
  initialValue,
  orgType,
  onValidatePromoCode,
  onPersistData,
  onSubmit,
}: UseOrgFormStepProps): UseOrgFormStepResult {
  const initialCountry: OrgFormCountry = isOrgFormCountry(initialValue.country)
    ? initialValue.country
    : 'US'

  const [orgName, setOrgNameState] = useState<string>(initialValue.orgName)
  const [country, setCountryState] = useState<OrgFormCountry>(initialCountry)
  const [state, setStateValue] = useState<string>(initialValue.state)
  const [promoCode, setPromoCodeState] = useState<string>(initialValue.promoCode)
  const [appliedPromo, setAppliedPromo] = useState<PromoCodeValidation | null>(null)
  const [appliedPromoCode, setAppliedPromoCode] = useState<string>('')
  const [isApplyingPromo, setIsApplyingPromo] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [fieldErrors, setFieldErrors] = useState<OrgFormFieldErrors>(EMPTY_FIELD_ERRORS)
  const [submitError, setSubmitError] = useState<string>('')

  const stateOptions = country === 'CA' ? CANADIAN_PROVINCES : US_STATES

  const setOrgName = useCallback((value: string) => {
    setOrgNameState(titleCase(value))
  }, [])

  const setCountry = useCallback((value: OrgFormCountry) => {
    setCountryState(value)
    setStateValue('')
  }, [])

  const setState = useCallback((value: string) => {
    setStateValue(value)
  }, [])

  const setPromoCode = useCallback((value: string) => {
    const upper = value.toUpperCase()
    setPromoCodeState(upper)
    setAppliedPromo(null)
    setAppliedPromoCode('')
  }, [])

  const promoApplied = appliedPromo !== null && appliedPromoCode === promoCode && promoCode !== ''

  const handleApplyPromo = useCallback(async () => {
    const code = promoCode.trim()
    if (code === '') return
    setFieldErrors((prev) => ({ ...prev, promoCode: '' }))
    setIsApplyingPromo(true)
    try {
      const result = await onValidatePromoCode({ code })
      if (!result.valid) {
        setAppliedPromo(null)
        setAppliedPromoCode('')
        setFieldErrors((prev) => ({ ...prev, promoCode: 'This promo code is not valid.' }))
        return
      }
      setAppliedPromo(result)
      setAppliedPromoCode(promoCode)
    } catch {
      setAppliedPromo(null)
      setAppliedPromoCode('')
      setFieldErrors((prev) => ({
        ...prev,
        promoCode: 'Could not validate the promo code. Please try again.',
      }))
    } finally {
      setIsApplyingPromo(false)
    }
  }, [promoCode, onValidatePromoCode])

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      const trimmedOrgName = orgName.trim()
      const errors: OrgFormFieldErrors = { ...EMPTY_FIELD_ERRORS }
      if (trimmedOrgName.length < 2) {
        errors.orgName = 'Enter an organization name with at least 2 characters.'
      }
      if (orgType !== 'crew' && state === '') {
        errors.state = country === 'CA' ? 'Select a province.' : 'Select a state.'
      }
      if (promoCode !== '' && !promoApplied) {
        errors.promoCode = 'Apply the promo code before continuing.'
      }

      const hasError = Object.values(errors).some((message) => message !== '')
      setFieldErrors(errors)
      setSubmitError('')
      if (hasError) return

      setIsSubmitting(true)
      try {
        const formData: OrgFormData = {
          orgName: trimmedOrgName,
          country,
          state,
          promoCode,
          ...(promoApplied && appliedPromo?.promotionCodeId
            ? { promotionCodeId: appliedPromo.promotionCodeId }
            : {}),
        }
        onPersistData(formData)
        onSubmit(formData)
      } finally {
        setIsSubmitting(false)
      }
    },
    [orgName, country, state, promoCode, promoApplied, appliedPromo, orgType, onPersistData, onSubmit],
  )

  return useMemo(
    () => ({
      orgName,
      country,
      state,
      promoCode,
      stateOptions,
      isSubmitting,
      isApplyingPromo,
      promoApplied,
      appliedPromo,
      fieldErrors,
      submitError,
      setOrgName,
      setCountry,
      setState,
      setPromoCode,
      handleApplyPromo,
      handleSubmit,
    }),
    [
      orgName,
      country,
      state,
      promoCode,
      stateOptions,
      isSubmitting,
      isApplyingPromo,
      promoApplied,
      appliedPromo,
      fieldErrors,
      submitError,
      setOrgName,
      setCountry,
      setPromoCode,
      handleApplyPromo,
      handleSubmit,
      setState,
    ],
  )
}
